import { CartUpdatedEvent } from '@theme/events';
import {
  buildGiftLineProperties,
  registerGiftConfig,
  unregisterGiftConfig,
  getRefused,
  setRefused,
  setGiftInCart,
  reconcileState,
  processGifts,
} from '@theme/auto-gift-processor';

// Claude 26/09 : message "plus que X € pour vos cadeaux" (code METHODE) dans le panier
const GIFT_MSG_SELECTOR = '.gift-threshold-methode';
let giftMsgTimer = null;

function formatEuros(cents) {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

async function refreshGiftMessages() {
  const nodes = document.querySelectorAll(GIFT_MSG_SELECTOR);
  if (nodes.length === 0) return;

  let cart;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    cart = await response.json();
  } catch {
    return;
  }

  nodes.forEach((node) => {
    const code = String(node.dataset.code || '').trim().toUpperCase();
    const threshold = parseInt(node.dataset.threshold, 10) || 0;
    const giftHandles = String(node.dataset.giftHandles || '').split(',');
    const hasCode = (cart.discount_codes || []).some((discount) => String(discount.code || '').trim().toUpperCase() === code);

    if (!hasCode) {
      node.hidden = true;
      node.textContent = '';
      return;
    }

    let others = 0;
    for (const item of cart.items) {
      if (item.properties?._is_gift === 'true' || giftHandles.includes(item.handle)) continue;
      others += item.final_line_price;
    }

    if (others < threshold) {
      node.style.background = '#FFD400';
      node.style.color = '#111111';
      node.textContent = `🎁 Code ${code} enregistré : plus que ${formatEuros(threshold - others)} d'achat et votre éponge Scrub + microfibre s'ajoutent gratuitement`;
    } else {
      node.style.background = '#111111';
      node.style.color = '#FFD400';
      node.textContent = '🎁 Bravo ! Votre éponge Scrub + microfibre sont offertes';
    }

    node.hidden = false;
  });
}

function scheduleGiftMessages() {
  clearTimeout(giftMsgTimer);
  giftMsgTimer = setTimeout(refreshGiftMessages, 150);
}

if (!window.__giftMsgObserver) {
  window.__giftMsgObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const added of mutation.addedNodes) {
        if (added.nodeType !== 1) continue;
        if (added.matches?.(GIFT_MSG_SELECTOR) || added.querySelector?.(GIFT_MSG_SELECTOR)) {
          scheduleGiftMessages();
          return;
        }
      }
    }
  });
  window.__giftMsgObserver.observe(document.documentElement, { childList: true, subtree: true });
  scheduleGiftMessages();
}

class AutoGift extends HTMLElement {
  constructor() {
    super();

    this.blockId = null;
    this.triggerType = 'cart_value';
    this.threshold = 0;
    this.quantityPerProduct = 1;
    this.collectionHandle = '';
    this.requiredCode = '';
    this.needsVariantChoice = false;
    this.giftBadge = '';
    this.products = [];

    this.dialog = null;
    this.dialogModal = null;
    this.confirmButton = null;

    this.popupOpen = false;
    this.popupValidated = false;

    this.confirmHandler = this.handleConfirm.bind(this);
    this.dialogCloseHandler = this.handleDialogClose.bind(this);
    this.variantSelectHandler = this.handleVariantSelectChange.bind(this);
  }

  connectedCallback() {
    this.blockId = this.dataset.blockId;
    this.triggerType = this.dataset.triggerType || 'cart_value';
    this.threshold = parseFloat(this.dataset.threshold) || 0;
    this.quantityPerProduct = parseInt(this.dataset.quantityPerProduct, 10) || 1;
    this.collectionHandle = this.dataset.collectionHandle || '';
    this.requiredCode = this.dataset.requiredCode || '';
    this.needsVariantChoice = this.dataset.needsVariantChoice === 'true';

    if (!this.#parseConfig()) return;

    this.dialog = this.querySelector('dialog-component');
    this.dialogModal = this.querySelector('[data-ref="popup-modal"]');
    this.confirmButton = this.querySelector('[data-ref="gift-confirm"]');

    this.confirmButton?.addEventListener('click', this.confirmHandler);
    this.dialogModal?.addEventListener('close', this.dialogCloseHandler);

    this.querySelectorAll('[data-ref="gift-variant-select"]').forEach((select) => {
      select.addEventListener('change', this.variantSelectHandler);
    });

    registerGiftConfig(
      this.blockId,
      {
        triggerType: this.triggerType,
        threshold: this.threshold,
        quantityPerProduct: this.quantityPerProduct,
        collectionHandle: this.collectionHandle,
        requiredCode: this.requiredCode,
        needsVariantChoice: this.needsVariantChoice,
        giftBadge: this.giftBadge,
        products: this.products,
      },
      this,
    );

    if (!this.requiredCode) this.#reconcileInitialState();

    if (this.needsVariantChoice) {
      queueMicrotask(() => {
        if (this.isConnected) this.#evaluateForPopup();
      });
    }

    // Claude 26/09 : cadeau lié à un code -> vérification au chargement (le code n'est visible que via /cart.js)
    if (this.requiredCode && this.dataset.initialGiftInCart !== 'true') {
      setTimeout(() => {
        if (this.isConnected) this.#autoAddOnLoad();
      }, 0);
    }
  }

  disconnectedCallback() {
    unregisterGiftConfig(this.blockId, this);
    this.confirmButton?.removeEventListener('click', this.confirmHandler);
    this.dialogModal?.removeEventListener('close', this.dialogCloseHandler);

    this.querySelectorAll('[data-ref="gift-variant-select"]').forEach((select) => {
      select.removeEventListener('change', this.variantSelectHandler);
    });
  }

  async #autoAddOnLoad() {
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionId = cartDrawer?.dataset.sectionId;
    const result = await processGifts(sectionId);
    if (!result) return;

    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
      return;
    }

    document.dispatchEvent(
      new CartUpdatedEvent(
        {},
        {
          itemCount: result.itemCount,
          source: 'auto-gift-code',
          sections: result.sections,
        },
      ),
    );
  }

  #parseConfig() {
    const configEl = this.querySelector('[data-ref="gift-config"]');
    if (!configEl) return false;

    try {
      const config = JSON.parse(configEl.textContent);
      this.giftBadge = config.giftBadge || '';
      this.products = config.products || [];
      return this.products.length > 0;
    } catch (error) {
      console.error('AutoGift: invalid config', error);
      return false;
    }
  }

  #reconcileInitialState() {
    const initialAbove = this.dataset.initialAboveThreshold === 'true';
    const initialGiftInCart = this.dataset.initialGiftInCart === 'true';

    reconcileState(this.blockId, initialAbove, initialGiftInCart);
  }

  #evaluateForPopup() {
    const above = this.dataset.initialAboveThreshold === 'true';
    const giftInCart = this.dataset.initialGiftInCart === 'true';
    const refused = getRefused(this.blockId);

    if (above && !giftInCart && !refused) {
      this.#openPopup();
    }
  }

  openPopupIfNeeded() {
    if (this.popupOpen) return;
    if (getRefused(this.blockId)) return;
    this.#openPopup();
  }

  #openPopup() {
    if (!this.dialog || !this.isConnected) return;

    this.popupOpen = true;
    this.popupValidated = false;

    this.querySelectorAll('[data-ref="gift-product-checkbox"]').forEach((checkbox) => {
      checkbox.checked = true;
    });

    this.dialog.open();
  }

  async handleConfirm(event) {
    event?.preventDefault();
    this.popupValidated = true;

    const variantCounts = new Map();

    for (const productEl of this.querySelectorAll('[data-ref="gift-product"]')) {
      const checkbox = productEl.querySelector('[data-ref="gift-product-checkbox"]');
      if (!checkbox?.checked) continue;

      const productId = productEl.dataset.productId;
      const product = this.products.find((p) => String(p.id) === String(productId));
      if (!product) continue;

      const select = productEl.querySelector('[data-ref="gift-variant-select"]');
      const variantId = select ? parseInt(select.value, 10) : product.defaultVariantId;

      variantCounts.set(variantId, (variantCounts.get(variantId) || 0) + 1);
    }

    if (variantCounts.size === 0) {
      setRefused(this.blockId, true);
      this.dialog?.close();
      return;
    }

    const items = [];

    for (const [variantId, quantity] of variantCounts) {
      items.push({
        id: variantId,
        quantity,
        properties: buildGiftLineProperties(this.blockId, this.giftBadge),
      });
    }

    this.confirmButton?.classList.add('is-loading');

    try {
      await this.#addItemsToCart(items);
    } finally {
      this.confirmButton?.classList.remove('is-loading');
      this.dialog?.close();
    }
  }

  handleDialogClose() {
    if (this.popupOpen && !this.popupValidated) {
      setRefused(this.blockId, true);
    }

    this.popupOpen = false;
    this.popupValidated = false;
  }

  handleVariantSelectChange(event) {
    const select = event.currentTarget;
    const selectedOption = select.options[select.selectedIndex];
    const newImage = selectedOption.dataset.image;
    if (!newImage) return;

    const productEl = select.closest('[data-ref="gift-product"]');
    const imageEl = productEl?.querySelector('[data-ref="gift-product-image"]');
    if (imageEl) imageEl.src = newImage;
  }

  async #addItemsToCart(items) {
    if (!this.isConnected) return;

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      const data = await response.json();

      if (!this.isConnected) return;

      if (data.status >= 400 || data.errors) {
        console.warn('AutoGift: failed to add gift items', data);
        return;
      }

      setGiftInCart(this.blockId, true);

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: data.item_count,
            source: 'auto-gift-popup',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      console.error('AutoGift: failed to add gift items', error);
    }
  }
}

if (!customElements.get('auto-gift')) {
  customElements.define('auto-gift', AutoGift);
}
